import React, { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import styled from 'styled-components'
import withApiData from '../HOC/withApiData'
import colors from '../../configuration/colors'
import { hideModal } from '../../actions/modal'
import Modal from '../mini/Modal'
import Button from '../mini/Buttons/Button'
import CancelButton from '../mini/Buttons/CancelButton'
import TextInputComponent from './CreateOrEditCourseModal/InputComponents/TextInputComponent'
import {
  createNewJourneyLevel,
  editJourneyLevel
} from '../mycourses/Levels/API'
import EditCourseIcon from '../../static/images/mycourses/icon_edit_course.svg'
import P2 from '../typography/paragraphs/P2'
import H2 from '../typography/headers/H2'
import MarginRight15 from '../typography/margins/MarginRight15'
import { excludeImageMDEOptions } from '../../utils/markdowneditor'

const ModalContainer = styled.div`
  width: 660px;
  border-radius: 10px;
  box-shadow: 0 2px 3.8px 0.2px rgba(0, 0, 0, 0.15);
  background-color: ${colors.smoke};
  margin: 180px auto;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 10px 30px 30px;
`

const MasteryBlock = styled.div`
  padding-top: 20px;
  display: flex;
  align-items: center;
  align-content: center;

  input, div {
    cursor: pointer;
  }
`

const CreateOrEditLevelModal = ({
  apiData,
  activityCourseId,
  journeyId,
  isCreateLevel,
  level,
  description
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const closeTheModal = () => {
    setTimeout(() => {
      dispatch(hideModal())
    }, 300)
  }

  const [levelName, setLevelName] = useState(isCreateLevel ? '' : level.name)
  const handleLevelName = e => {
    setLevelName(e.target.value)
  }

  const [levelDescription, setLevelDescription] = useState(
    isCreateLevel ? '' : description
  )
  const handleLevelDescription = e => {
    let event = {
      target: {}
    }
    event.target.id = 'journey-description'
    event.target.value = e

    setLevelDescription(event.target.value)
  }

  const [levelMastery, setLevelMastery] = useState(
    isCreateLevel ? false : level.mastery
  )
  const handleLevelMastery = e => {
    const master = levelMastery
    setLevelMastery(!master)
  }

  const createMutation = useMutation(
    ({ levelName, levelDescription, levelMastery }) =>
      createNewJourneyLevel(
        apiData,
        activityCourseId,
        journeyId,
        levelName,
        levelDescription,
        levelMastery
      )
  )

  const editLevelMutation = useMutation(
    ({ levelId, levelName, levelDescription, levelMastery }) =>
      editJourneyLevel(
        apiData,
        activityCourseId,
        journeyId,
        levelId,
        levelName,
        levelDescription,
        levelMastery
      )
  )

  const couldSaveData = levelName.length > 0 && levelDescription.length > 0

  const handleSaveNewLevel = () => {
    if (couldSaveData) {
      isCreateLevel
        ? createMutation.mutate(
            {
              apiData,
              activityCourseId,
              journeyId,
              levelName,
              levelDescription,
              levelMastery
            },
            {
              onSuccess: data => {
                queryClient.setQueryData(
                  ['journey-levels-' + journeyId],
                  levels => {
                    const newLevels = [...levels]
                    newLevels.push({ ...data, ...{ just_created: true } })
                    return newLevels
                  }
                )

                setTimeout(() => {
                  closeTheModal()
                }, 300)
              }
            }
          )
        : editLevelMutation.mutate(
            {
              apiData,
              activityCourseId,
              journeyId,
              levelId: level.id,
              levelName,
              levelDescription,
              levelMastery
            },
            {
              onSuccess: async () => {
                await queryClient.refetchQueries([
                  'journey-levels-' + journeyId
                ])
                await queryClient.refetchQueries(['journey-level-' + level.id])
                setTimeout(() => {
                  closeTheModal()
                }, 300)
              }
            }
          )
    }
  }

  const levelDescriptionOptions = Object.assign({}, excludeImageMDEOptions)
  levelDescriptionOptions.initialValue = levelDescription
  levelDescriptionOptions.placeholder = 'Level Description'
  levelDescriptionOptions.autofocus = false

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        <HeaderContainer>
          {isCreateLevel ? (
            <H2>+ New Level</H2>
          ) : (
            <H2>
              <span style={{ marginRight: '12px' }}>
                <img src={EditCourseIcon} />
              </span>
              Edit Level
            </H2>
          )}
          <FlexContainer>
            <MarginRight15>
              <CancelButton
                handleClick={closeTheModal}
                title="Cancel"
                width="104px"
              />
            </MarginRight15>
            <div
              data-for="createLevelTooltip"
              data-tip="Please enter all required fields prior to creating this level."
            >
              <Button
                width="104px"
                title={isCreateLevel ? 'Create' : 'Save'}
                handleClick={handleSaveNewLevel}
                buttonColor={colors.oeYellow}
                borderColor={colors.oeYellow}
                hoverBackground={colors.charcoal4}
                hoverColor={colors.white}
                couldSaveData={couldSaveData}
                shouldShowTooltipAndDisableButton={true}
              />
              {!couldSaveData && (
                <ReactTooltip
                  id="createLevelTooltip"
                  place="bottom"
                  effect="solid"
                  className="activityCourseTooltip"
                />
              )}
            </div>
          </FlexContainer>
        </HeaderContainer>
        <MainContainer>
          <TextInputComponent
            title="Level Name"
            placeholder="Level Name"
            handleEnteredTextValue={handleLevelName}
            value={levelName}
            shouldDisplayAsteriskSymbol={true}
            shouldBeTextarea={false}
          />
          <MasteryBlock>
            <input
              type="checkbox"
              id="mastery-level"
              name="masteryLevel"
              style={{ marginRight: '15px' }}
              checked={levelMastery}
              onChange={handleLevelMastery}
            />
            <P2 onClick={handleLevelMastery}>Mastery Level</P2>
          </MasteryBlock>
          <TextInputComponent
            title="Level Description"
            placeholder="Level Description"
            handleEnteredTextValue={handleLevelDescription}
            hintText="1-2 sentence summary of the level and what students will learn and do."
            value={levelDescription}
            shouldDisplayAsteriskSymbol={true}
            shouldBeTextarea={true}
            options={levelDescriptionOptions}
          />
        </MainContainer>
      </ModalContainer>
    </Modal>
  )
}

CreateOrEditLevelModal.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourseId: PropTypes.string.isRequired,
  journeyId: PropTypes.string.isRequired,
  isCreateLevel: PropTypes.bool.isRequired,
  level: PropTypes.object,
  description: PropTypes.string
}

export default withApiData(CreateOrEditLevelModal)



// WEBPACK FOOTER //
// ./src/components/modal/CreateOrEditLevelModal.js